import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.jsx';
import { announceEntry, deleteEntry, whatsNewPrefillFor } from '../../lib/bibliography.js';

export default function EntryDetailModal({ entry, onClose, onDeleted, onUpdated }) {
  const { t } = useTranslation();
  const { isAdmin, isActive } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const [error, setError] = useState(null);

  // Admins flag the row (the DB trigger posts the notice immediately);
  // collaborators are taken to a prefilled What's New form, which goes
  // through the usual review.
  async function handleAnnounce() {
    if (!isAdmin) {
      onClose();
      navigate('/whats-new/new', { state: { prefill: whatsNewPrefillFor(entry) } });
      return;
    }
    setAnnouncing(true);
    setError(null);
    try {
      const updated = await announceEntry(entry.id);
      onUpdated?.(updated);
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setAnnouncing(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await deleteEntry(entry.id);
      onClose();
      onDeleted?.(entry.id);
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setDeleting(false);
    }
  }

  function handleEdit() {
    onClose();
    navigate(`/bibliography/${entry.id}/edit`);
  }

  useEffect(() => {
    if (!entry) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [entry, onClose]);

  if (!entry) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-navy-deep/80 p-4 backdrop-blur"
      onClick={onClose}
    >
      <div
        className="relative my-12 w-full max-w-3xl rounded-xl border border-white/10 bg-navy-base/95 p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full border border-white/10 bg-navy-mid/40 px-3 py-1 text-sm text-ink-secondary hover:border-gold/40 hover:text-gold-light"
          aria-label={t('common.cancel')}
        >
          ×
        </button>

        <div className="mb-2 flex flex-wrap items-baseline gap-3">
          <span className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gold">
            {t(`bibliography.types.${entry.type}`)}
          </span>
          {entry.year && <span className="text-sm text-ink-muted">{entry.year}</span>}
          {entry.language_display && (
            <span className="text-sm text-ink-muted">· {entry.language_display}</span>
          )}
        </div>

        <h2 className="font-serif text-2xl font-semibold leading-snug text-cream">
          {entry.title}
        </h2>

        {entry.authors?.length > 0 && (
          <p className="mt-2 text-base text-ink-secondary">{entry.authors.join(', ')}</p>
        )}

        <dl className="mt-6 grid grid-cols-1 gap-y-3 text-sm md:grid-cols-[120px_1fr]">
          <Field label={t('bibliography.detail.editors')} value={entry.editors?.join(', ')} />
          <Field label={t('bibliography.detail.journal')} value={entry.journal} />
          <Field label={t('bibliography.detail.booktitle')} value={entry.booktitle} />
          <Field label={t('bibliography.detail.conference')} value={entry.conference} />
          <Field label={t('bibliography.detail.publisher')} value={entry.publisher} />
          <Field label={t('bibliography.detail.place')} value={entry.place} />
          <Field label={t('bibliography.detail.edition')} value={entry.edition} />
          <Field label={t('bibliography.detail.volume')} value={entry.volume} />
          <Field label={t('bibliography.detail.issue')} value={entry.issue} />
          <Field label={t('bibliography.detail.pages')} value={entry.pages} />
          <Field label={t('bibliography.detail.doi')} value={entry.doi} doi />
          <Field label={t('bibliography.detail.url')} value={entry.url} link />
        </dl>

        {entry.abstract && (
          <section className="mt-6">
            <h3 className="mb-2 text-[0.72rem] font-semibold uppercase tracking-[0.15em] text-gold">
              {t('bibliography.detail.abstract')}
            </h3>
            <p className="whitespace-pre-line text-sm leading-relaxed text-ink-primary">
              {entry.abstract}
            </p>
          </section>
        )}

        {entry.keywords?.length > 0 && (
          <section className="mt-6">
            <h3 className="mb-2 text-[0.72rem] font-semibold uppercase tracking-[0.15em] text-gold">
              {t('bibliography.detail.keywords')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {entry.keywords.map((k) => (
                <span
                  key={k}
                  className="rounded-full border border-white/10 bg-navy-mid/40 px-3 py-1 text-xs text-ink-secondary"
                >
                  {k}
                </span>
              ))}
            </div>
          </section>
        )}

        {isActive && (
          <footer className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-white/5 pt-6">
            {error && (
              <p className="mr-auto text-sm text-red-300">{error}</p>
            )}
            {confirmDelete ? (
              <>
                <span className="text-sm text-ink-secondary">
                  {t('bibliography.detail.confirmDelete')}
                </span>
                <button
                  type="button"
                  className="btn-ghost text-sm"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-red-500/50 bg-red-950/30 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? t('common.loading') : t('common.delete')}
                </button>
              </>
            ) : (
              <>
                {entry.announce_in_whats_new && isAdmin ? (
                  <span className="mr-auto text-xs uppercase tracking-[0.12em] text-ink-muted">
                    {t('bibliography.detail.inWhatsNew')}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost mr-auto text-sm"
                    onClick={handleAnnounce}
                    disabled={announcing}
                  >
                    {announcing ? t('common.loading') : t('bibliography.detail.addToWhatsNew')}
                  </button>
                )}
                <button type="button" className="btn-ghost text-sm" onClick={handleEdit}>
                  {isAdmin
                    ? t('bibliography.detail.editEntry')
                    : t('bibliography.detail.suggestEdit')}
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className="rounded-md border border-red-500/40 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
                    onClick={() => setConfirmDelete(true)}
                  >
                    {t('common.delete')}
                  </button>
                )}
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, link, doi }) {
  if (!value) return null;
  let display = value;
  if (link || doi) {
    const href = doi ? `https://doi.org/${value}` : value;
    display = (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gold-light hover:underline"
      >
        {value}
      </a>
    );
  }
  return (
    <>
      <dt className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">
        {label}
      </dt>
      <dd className="text-ink-primary">{display}</dd>
    </>
  );
}
